import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Subject, takeUntil } from 'rxjs';
import { validateUpload } from 'src/app/guards/file.guard';
import { IAddCarePlanFile } from 'src/app/interfaces/CarePlanFile/IAddCarePlanFile';
import { ICarePlanFileDropdown } from 'src/app/interfaces/CarePlanFile/ICarePlanFileDropdown';
import { CarePlanFileService } from 'src/app/services/carePlanFile.service';
import { SwalToastService } from 'src/app/services/swal.service';
import { dateRangeValidator } from 'src/date/date.configuration';

@Component({
  selector: 'app-care-plan-file-add',
  templateUrl: './care-plan-file-add.component.html',
  styleUrl: './care-plan-file-add.component.css',
  standalone: false,
})
export class CarePlanFileAddComponent {
  @ViewChild('fileInput', { static: false })
  fileInput?: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  carePlanFileDropdown: ICarePlanFileDropdown | undefined;

  fileObj?: File;

  isVisible = false;
  isLoading = false;

  isEdit = false;
  editProblemsId?: number;

  carePlanFileSubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  carePlanFileForm: FormGroup = new FormGroup({});

  constructor(
    private readonly carePlanFileService: CarePlanFileService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.isEdit = false;

    this.carePlanFileService.getCarePlanFileDropdown().subscribe({
      next: (pro) => {
        this.carePlanFileDropdown = pro?.data;
        this.isLoading = false;
      },
      error: (err) => {},
    });

    this.carePlanFileForm = this.formBuilder.group({
      dateValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      descriptionValue: [null, Validators.required],
      fileValue: [null, Validators.required],
    });

    this.carePlanFileService.carePlanFileEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.editProblemsId = data?.id;

          this.carePlanFileForm.patchValue({
            dateValue: data?.date,
            descriptionValue: data?.description,
          });
        } else {
          this.isEdit = false;
          this.editProblemsId = undefined;

          this?.carePlanFileForm.reset();

          this.carePlanFileForm.patchValue({
            dateValue: this?.getCurrentDate(),
          });
        }
      });

    this.carePlanFileService.carePlanFileModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    // Unsubscribe from observables
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  handleCancel() {
    this.carePlanFileForm.reset();

    this.carePlanFileService.carePlanFileModalSub.next(false);

    this.carePlanFileSubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.carePlanFileSubmitted = true;

    if (this.fileObj && this.carePlanFileForm.valid) {
      const carePlanObj: IAddCarePlanFile = {};

      carePlanObj.id = this.editProblemsId;
      carePlanObj.date = this.carePlanFileForm.value?.dateValue;
      carePlanObj.description = this.carePlanFileForm.value?.descriptionValue;
      carePlanObj.file = this.fileObj;

      if (this.isEdit) {
        this.carePlanFileService.editCarePlanFile(carePlanObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Care Plan File edited successfully'
            );
            this.carePlanFileService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to edit Care Plan File');
            this.handleCancel();
          },
        });
      } else {
        this.carePlanFileService.addCarePlanFile(carePlanObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Care Plan File added successfully'
            );
            this.carePlanFileService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add Care Plan File');
            this.handleCancel();
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }

  async onFileSelected(event: any) {
    const file: File | null = event.target.files[0];
    const result = await validateUpload(file);
    if (!result.ok) {
      this.swalToastService.toastError(result.error!);
      this.fileObj = undefined;
      this.carePlanFileForm.get('fileValue')?.setValue(undefined);
      this.carePlanFileForm?.get('fileValue')?.setErrors({ invalid: true });
      // Physically clear the native file input so the same file can be re-selected
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      return;
    }
    this.fileObj = file!;
    this.carePlanFileForm.get('fileValue')?.setValue(file?.name);
    this.carePlanFileForm.get('fileValue')?.setErrors(null);
  }
}
